// UI 1: the slice map on top, the timeline of the rows you expand below, then the
// stalls list and the detail panel.

import { el, $, fmtPs } from "../../dom.js";
import { Timeline } from "../../timeline.js";
import { SliceMap } from "../../slicemap.js";
import { Detail } from "../../detail.js";
import { Stalls } from "../../stalls.js";
import { rowKeyOf } from "../../links.js";
import { unitColor } from "../../isa.js";
import { ChipPage } from "../../chippage.js";
import { openGuide } from "../../guide.js";

export class App {
  constructor(ctx) {
    this.ctx = ctx;
    this.model = ctx.model;
    this.links = ctx.links;
    this.store = ctx.store;
    this.offs = [];
    this.expandedChips = new Set();
    this.openCores = new Set();
    this.sub = { units: false, dma: true, flags: false };
    this.filter = "";
    this.showMap = true;
    this.showStalls = false;   // the list is there for when a stall is the question
    const m = this.model;
    for (const chip of m.chipIds) if (m.coresOfChip(chip).some((c) => c.n) || m.scsOfChip(chip).length) this.expandedChips.add(chip);
    if (m.chipIds.length > 4) {
      // Many chips: open only the busiest two by default.
      const busiest = [...m.chipIds].sort((a, b) => (m.chipTend.get(b) || 0) - (m.chipTend.get(a) || 0)).slice(0, 2);
      this.expandedChips = new Set(busiest);
    }
  }

  mount(root) {
    window.__app = this;   // for the headless tests
    this.root = root;
    root.classList.add("app-timeline");
    this.dash = el("div", "dash");
    this.chipEl = el("div", "drill-chippage");
    this.chipEl.hidden = true;
    root.appendChild(this.dash);
    root.appendChild(this.chipEl);
    this.page = "dash";
    this.chipPage = new ChipPage(this.chipEl, this.ctx, {
      onBack: () => this.showDash(),
      onChange: () => this.syncHash(),
      brush: () => [this.tl.t0, this.tl.t1],
    });
    this.mapEl = el("div");
    this.toolbar = el("div", "toolbar");
    this.miniEl = el("div", "minimap");
    this.tlEl = el("div");
    this.bottom = el("div", "bottom");
    this.stallsEl = el("div", "bottom-left panel");
    this.stallsEl.hidden = !this.showStalls;
    this.detailEl = el("div", "bottom-right panel");
    this.bottom.appendChild(this.stallsEl);
    this.bottom.appendChild(this.detailEl);
    this.dash.appendChild(this.mapEl);
    this.dash.appendChild(this.toolbar);
    this.dash.appendChild(this.miniEl);
    this.dash.appendChild(this.tlEl);
    this.dash.appendChild(this.bottom);

    this.map = new SliceMap(this.mapEl, this.ctx, {
      stripPx: Math.max(140, Math.min(300, Math.floor((root.clientWidth - 40) / Math.max(1, this.model.shape[0]) - 90))),
      brushRoot: this.miniEl,
      onBrush: (t0, t1) => this.tl.setView(t0, t1, true),
      onCoreClick: (chip, r, t) => { this.focusRow(chip, r.id, t); this.detail.show({ kind: "row", chip, core: r.core || null, label: r.label }); },
      onChipClick: (chip) => this.toggleChip(chip),
      onCellClick: (c) => this.selectDma(c.dmas[0]),
    });
    this.tl = new Timeline(this.tlEl, this.ctx, {
      onView: (t0, t1) => { this.map.setBrush(t0, t1, false); this.stalls.setWindow(t0, t1); this.syncHash(); },
      onSelect: (hit, t) => this.onSelect(hit, t),
      onCursor: (t) => this.sweep(t),
    });
    this.detail = new Detail(this.detailEl, this.ctx, {
      expand: (item) => this.expandTo(item),
      select: (item) => this.select(item),
      jump: (rowId, t0, t1, item) => this.jump(rowId, t0, t1, item),
      arrow: (w, r) => this.arrowFor(w, r),
    });
    this.stalls = new Stalls(this.stallsEl, this.ctx, { onPick: (w, r, row) => this.pickWait(w, r, row) });
    this.buildToolbar();
    this.rebuildRows();
    this.tl.fitAll();
    this.offs.push(this.store.on("hlRow", (s) => this.tl.highlightRow(s.hlRow)));
    this.offs.push(this.store.on("hover", (s) => this.map.highlightRow(s.hover ? s.hover.row : null)));
    this.keys = (e) => this.onKey(e);
    document.addEventListener("keydown", this.keys);
  }

  unmount() {
    document.removeEventListener("keydown", this.keys);
    this.chipPage.destroy();
    for (const off of this.offs) off();
    this.tl.destroy();
    this.map.destroy();
    this.root.classList.remove("app-timeline");
    this.root.textContent = "";
  }

  redraw() { this.tl.invalidate(3); this.map.render(); this.chipPage.redraw(); }

  // --- the chip page ---------------------------------------------------------------

  // The chip page for whatever the detail panel shows, with the cursor on it.
  expandTo(hit) {
    const mid = (this.tl.t0 + this.tl.t1) / 2;
    if (hit.kind === "row") this.openChip(hit.chip, hit.core ? hit.core.key : null, mid);
    else if (hit.kind === "bundle" || hit.kind === "nop" || hit.kind === "unit") this.openChip(hit.core.chip, hit.core.key, hit.core.t[hit.bi ?? 0] ?? mid);
    else if (hit.kind === "wait") this.openChip(hit.wait.chip, rowKeyOf(hit.wait.chip, hit.wait.core, hit.wait.ci, hit.wait.tile), hit.wait.t0, hit);
    else if (hit.kind === "stall") this.openChip(hit.stall.chip, hit.stall.core.key, hit.stall.t0, hit);
    else if (hit.kind === "dma") this.openChip(hit.dma.chip, null, hit.dma.at, hit);
    else if (hit.kind === "flag") this.openChip(hit.flag.chip, rowKeyOf(hit.flag.chip, hit.flag.core, hit.flag.ci, hit.flag.tile), hit.flag.at, hit);
    else if (hit.kind === "host") this.openChip(hit.host.chip, null, hit.host.ps, hit);
    else if (hit.kind === "scdispatch") this.openChip(hit.sc.chip, `c${hit.sc.chip}s${hit.sc.ci}`, hit.sc.ps, hit);
  }

  openChip(chip, tc = null, t = null, item = null) {
    this.page = "chip";
    // The way back lives in the page header, where a reader looks for it.
    const back = document.getElementById("back");
    back.hidden = false;
    back.onclick = () => this.showDash();
    this.dash.hidden = true;
    this.chipEl.hidden = false;
    this.chipPage.open(chip, tc, t, item);
    this.syncHash();
  }

  showDash() {
    this.page = "dash";
    document.getElementById("back").hidden = true;
    this.chipPage.destroy();
    this.chipEl.hidden = true;
    this.dash.hidden = false;
    this.tl.invalidate(3);
    this.syncHash();
  }

  buildToolbar() {
    const t = this.toolbar;
    // Three groups, each labelled by what its buttons act on: the time window, the
    // rows, and what is shown beside them. Toggles keep their state visible.
    const group = (label) => { const g = el("span", "grp"); g.appendChild(el("span", "glbl", label)); t.appendChild(g); return g; };
    const btn = (g, label, title, fn, cls) => { const b = el("button", cls, label); b.title = title; b.onclick = fn; g.appendChild(b); return b; };
    const toggle = (g, label, title, get, set) => { const b = btn(g, label, title, () => { set(!get()); b.classList.toggle("on", get()); }, get() ? "on" : ""); return b; };
    const time = group("time");
    btn(time, "fit", "fit the cores' activity (f)", () => this.tl.fitAll());
    btn(time, "whole run", "the whole run, host bring-up included", () => this.tl.fitRun());
    btn(time, "◀ busy", "the previous stretch where any core was issuing (p)", () => this.busy(-1));
    btn(time, "busy ▶", "the next stretch where any core was issuing (n)", () => this.busy(1));
    btn(time, "zoom to selection", "zoom to the selected wait, DMA or command (z)", () => this.zoomSelection());
    const rows = group("rows");
    btn(rows, "expand all", "every chip open", () => { for (const c of this.model.chipIds) this.expandedChips.add(c); this.rebuildRows(); });
    btn(rows, "collapse all", "every chip as one row", () => { this.expandedChips.clear(); this.rebuildRows(); });
    const inp = el("input");
    inp.placeholder = "filter: chip 2, TC1, SC, host, flag 507";
    inp.size = 30;
    inp.oninput = () => { this.filter = inp.value.trim(); this.rebuildRows(); };
    this.filterInp = inp;
    rows.appendChild(inp);
    const show = group("show");
    this.subBtns = {};
    for (const [k, label, title] of [["units", "unit ops", "a sub-row per core with its MXU / XLU / EUP / PRNG operations (1)"], ["dma", "DMAs", "a sub-row per core with the DMAs it issued (2)"]]) {
      if (k === "units" && !this.model.hasUnits) continue;
      this.subBtns[k] = btn(show, label, title, () => this.toggleSub(k), this.sub[k] ? "on" : "");
    }
    toggle(show, "slice map", "the chips' overview above the timeline", () => this.showMap, (v) => { this.showMap = v; this.mapEl.hidden = !v; });
    toggle(show, "stalls", "the stalls list below the timeline", () => this.showStalls, (v) => { this.showStalls = v; this.stallsEl.hidden = !v; });
    // The ? opens the guide: the colours, the rows and the keys, spelled out.
    const help = el("button", "help guide-open", "?");
    help.title = "how to read this page: the colours, the rows, the keys (?)";
    help.onclick = () => openGuide();
    t.appendChild(help);
  }

  toggleSub(k) {
    if (!this.subBtns[k]) return;
    this.sub[k] = !this.sub[k];
    this.subBtns[k].classList.toggle("on", this.sub[k]);
    this.rebuildRows();
  }

  // --- rows ------------------------------------------------------------------------

  rows() {
    const m = this.model, rows = [];
    const f = this.filter.toLowerCase();
    const flagM = /^flag\s+(\d+)$/.exec(f);
    const keep = (label, extra = "") => !f || flagM || (label + " " + extra).toLowerCase().includes(f);
    const touchesFlag = (rowId) => {
      if (!flagM) return true;
      const idx = +flagM[1];
      return (this.links.waitsByRow.get(rowId) || []).some((i) => m.waits[i].idx === idx) || (this.links.flagsByRow.get(rowId) || []).some((i) => m.flags[i].idx === idx);
    };
    const chips = [...m.chipIds];
    for (const chip of chips) {
      const label = m.chipLabel(chip);
      const cores = m.coresOfChip(chip), scs = m.scsOfChip(chip);
      const exp = this.expandedChips.has(chip);
      const chipRow = { id: "chip:" + chip, kind: "chip", chip, label, expanded: exp, depth: 0, toggle: () => this.toggleChip(chip), click: () => { this.toggleChip(chip); this.detail.show({ kind: "row", chip, label }); }, title: `${label}: ${cores.length} TensorCores with a program, ${scs.length} SparseCores seen` };
      if (!exp) { if (keep(label, "chip") && touchesFlag("chip:" + chip)) rows.push(chipRow); continue; }
      const sub = [];
      if (keep(`${label} host`, "host") && (!flagM || (m.hostByChip.get(chip) || []).some((h) => h.kind === "sync_flag" && h.idx === +flagM[1]))) sub.push({ id: `c${chip}host`, kind: "host", chip, label: "host", depth: 1, title: "firmware commands, doorbells and interrupts of chip " + chip });
      // A bundle stream: a TensorCore, a SparseCore sequencer, or one of its tiles.
      // What a stream row could open into; a chevron only when there is something.
      const streamRow = (c, depth) => {
        const open = this.openCores.has(c.key);
        const waits = (this.links.waitsByRow.get(c.key) || []).length;
        const tiles = c.kind === "sc" ? m.tilesOf(chip, c.ci) : [];
        const hasDma = (this.links.dmaByIssuer.get(c.key) || []).length > 0;
        const subs = [];
        if (this.sub.units && c.units.length) subs.push({ id: c.key + ".units", kind: "tc.units", chip, core: c, label: "units", depth: depth + 1, title: "MXU / XLU / EUP / PRNG operations, at the bundle that issued them" });
        if (this.sub.dma && hasDma) subs.push({ id: c.key + ".dma", kind: "tc.dma", chip, core: c, label: "dma", depth: depth + 1, title: "DMAs this core issued: issue → start → landed" });
        const expandable = subs.length > 0 || tiles.length > 0;
        const toggle = expandable ? () => { if (open) this.openCores.delete(c.key); else this.openCores.add(c.key); this.rebuildRows(); } : null;
        sub.push({
          id: c.key, kind: "tc", chip, ci: c.ci, core: c, label: c.kind === "tile" ? `tile ${c.tile}` : c.short, expanded: open, depth,
          toggle,
          // A click anywhere on the row opens it, not only on the chevron.
          click: () => { this.store.set({ hlRow: c.key }); if (toggle) toggle(); this.detail.show({ kind: "row", chip, core: c, label: c.label }); },
          title: `${c.label}: ${c.n.toLocaleString()} bundles, ${waits} waits, ${c.cyclePs.toFixed(0)} ps/cycle${tiles.length ? ", " + tiles.length + " tiles (open for each)" : ""}`,
          badge: c.unsynced ? "unsynced" : tiles.length ? `${tiles.length} tiles` : null, badgeWarn: !!c.unsynced,
          badgeTitle: c.unsynced ? "this core never joined another clock through a flag: its alignment to other rows is arbitrary" : "click to open a row per tile",
          height: c.kind === "tile" ? 20 : c.kind === "sc" && tiles.length ? 40 : undefined,
        });
        if (open) {
          sub.push(...subs);
          for (const t of tiles) if (keep(`${label} ${t.label}`, "tile") && touchesFlag(t.key)) streamRow(t, depth + 1);
        }
      };
      for (const c of cores) {
        if (!keep(`${label} ${c.label} TC${c.ci}`, "tc tensorcore") || !touchesFlag(c.key)) continue;
        streamRow(c, 1);
      }
      for (const c of m.scCoresOfChip(chip)) {
        if (!keep(`${label} ${c.label} SC${c.ci} sparsecore`, "sc") || !touchesFlag(c.key)) continue;
        streamRow(c, 1);
      }
      const streamed = new Set(m.scCoresOfChip(chip).map((c) => c.ci));
      for (const ci of scs) {
        if (streamed.has(ci)) continue;
        const id = `c${chip}s${ci}`;
        if (!keep(`${label} SC${ci} sparsecore`, "sc") || !touchesFlag(id)) continue;
        sub.push({ id, kind: "sc", chip, ci, label: `SC${ci}`, depth: 1, title: `SparseCore ${ci} of chip ${chip}: dispatches, waits, flag writes, DMAs (no bundle stream in this trace)` });
      }
      if (!scs.length && m.scPerChip && keep(`${label} sparsecore`, "sc") && !flagM) sub.push({ id: `c${chip}snone`, kind: "sc", chip, ci: -1, label: `SC ×${m.scPerChip}`, title: "this chip's SparseCores left no events", height: 14 });
      if (sub.length || keep(label, "chip")) rows.push(chipRow, ...sub);
    }
    return rows;
  }

  rebuildRows() {
    const rows = this.rows();
    this.tl.setRows(rows);
    this.dash.style.setProperty("--tl-rows", this.tl.total + "px");   // the timeline's own height (drill.css)
    // The stalls list holds a tile's waits back until its row is on screen: attributing
    // them walks the streams (links.js), and a slice has a quarter of a million.
    if (this.stalls) this.stalls.setTileRows(rows.filter((r) => r.core && r.core.kind === "tile").map((r) => r.id));
    this.syncHash();
  }

  toggleChip(chip) {
    if (this.expandedChips.has(chip)) this.expandedChips.delete(chip); else this.expandedChips.add(chip);
    this.rebuildRows();
  }

  // Show a row (expanding its chip), scroll to it, highlight it; optionally centre a time.
  focusRow(chip, rowId, t) {
    if (!this.expandedChips.has(chip)) { this.expandedChips.add(chip); this.rebuildRows(); }
    if (rowId.includes(".") ) {
      const key = rowId.split(".")[0];
      if (!this.openCores.has(key)) { this.openCores.add(key); this.rebuildRows(); }
    }
    this.tl.scrollToRow(rowId);
    this.store.set({ hlRow: rowId });
    if (t != null) {
      const span = this.tl.t1 - this.tl.t0;
      this.tl.setView(t - span / 2, t + span / 2);
    }
  }

  // --- navigation ------------------------------------------------------------------

  // Jump to the previous / next stretch where any core was issuing.
  busy(dir) {
    const st = this.ctx.bins.stretches();
    if (!st.length) return;
    const tl = this.tl, mid = (tl.t0 + tl.t1) / 2;
    let pick = null;
    if (dir > 0) pick = st.find((s) => s.t0 > tl.t1 - (tl.t1 - tl.t0) * 0.05) || st[st.length - 1];
    else { for (const s of st) if (s.t1 < tl.t0 + (tl.t1 - tl.t0) * 0.05) pick = s; if (!pick) pick = st[0]; }
    void mid;
    tl.zoomTo(pick.t0, pick.t1, 0.1);
  }

  zoomSelection() {
    const s = this.store.get().selection, m = this.model;
    if (!s) return;
    let a, b;
    if (s.kind === "wait") { a = s.wait.t0; b = s.wait.open ? m.tEnd : s.wait.t1; }
    else if (s.kind === "stall") { a = s.stall.t0; b = s.stall.open ? m.tEnd : s.stall.t1; }
    else if (s.kind === "dma") { a = Number.isNaN(s.dma.at) ? s.dma.start : s.dma.at; b = s.dma.open ? a : s.dma.end; }
    else if (s.kind === "host") { [a, b] = s.host.span || [s.host.ps, s.host.ps]; }
    else if (s.kind === "bundle" || s.kind === "nop") { a = s.core.t[s.bi ?? 0]; b = a + s.core.cyclePs * 40; a -= s.core.cyclePs * 40; }
    else return;
    this.tl.zoomTo(a, b, 0.2);
  }

  // --- selection -------------------------------------------------------------------

  // The cursor line dragged: the detail panel follows it along the selected row,
  // showing the bundle under the cursor as it moves.
  sweep(t) {
    const st = this.store.get(), sel = st.selection;
    const key = sel && sel.core ? sel.core.key : sel && sel.row ? String(sel.row).split(".")[0] : st.hlRow;
    const core = key ? this.model.coreByKey.get(key) : null;
    if (!core || !core.n) return;
    const hit = { kind: "bundle", row: core.key, core, bi: Math.max(0, this.model.bundleAt(core, t)) };
    this.store.set({ selection: hit });
    this.detail.show(hit, t);
    this.tl.invalidate(2);
  }

  onSelect(hit, t) {
    this.tl.setArrows([]);
    this.detail.show(hit, t);
    if (hit && hit.kind === "wait") this.stalls.select(hit.wait);
    this.syncHash();
  }

  select(item) {
    this.focusRow(this.chipOfRow(item.row), item.row);
    this.store.set({ selection: item });
    this.tl.setArrows([]);
    this.detail.show(item);
    this.tl.invalidate(2);
    this.syncHash();
  }

  chipOfRow(rowId) {
    const m = /^c(\d+)/.exec(rowId);
    return m ? +m[1] : this.model.chipIds[0];
  }

  jump(rowId, t0, t1, item) {
    this.focusRow(this.chipOfRow(rowId), rowId);
    this.tl.zoomTo(t0, t1, 0.1);
    if (item) this.select(item);
  }

  selectDma(d) {
    const row = d.byKind === "tc" ? `c${d.chip}t${d.byCi}.dma` : d.byKind === "sc" ? `c${d.chip}s${d.byCi}` : `c${d.chip}host`;
    this.jump(row, Number.isNaN(d.at) ? d.start : d.at, d.open ? this.model.tEnd : d.end, { kind: "dma", row, dma: d });
  }

  pickWait(w, r, row) {
    const pad = Math.max(w.stallPs, 20 * this.model.defaultCycle());
    const t1 = w.open ? this.model.tEnd : w.t1;
    this.focusRow(w.chip, row);
    this.tl.zoomTo(w.t0 - pad * 0.2, t1 + pad * 0.2, 0);
    this.select(w.reason != null ? { kind: "stall", row, stall: w } : { kind: "wait", row, wait: w });
  }

  arrowFor(w, r) {
    const toRow = rowKeyOf(w.chip, w.core, w.ci);
    const fromT = r.how === "dma" ? r.dma.end : r.flag ? r.flag.at : w.t1;
    let fromRow = r.fromRow;
    // A DMA arrow starts on the issuer's DMA sub-row when it is shown, else its core row.
    if (r.how === "dma" && r.dma.byKind === "tc" && !this.tl.rows.some((x) => x.id === fromRow + ".dma")) fromRow = r.fromRow;
    else if (r.how === "dma" && r.dma.byKind === "tc") fromRow = fromRow + ".dma";
    const fromLabel = r.how === "dma" ? `DMA #${r.dma.id} from ${fromRow}` : r.how === "core" ? r.core.label : r.how === "host" ? `chip ${w.chip} host` : fromRow;
    this.tl.setArrows([{ fromRow, fromT, toRow, toT: w.open ? this.model.tEnd : w.t1, fromLabel }]);
  }

  // --- keys ------------------------------------------------------------------------

  onKey(e) {
    if (e.target.tagName === "INPUT" || e.target.tagName === "SELECT") return;
    if (this.page === "chip") { this.chipPage.onKey(e); return; }
    const tl = this.tl, s = this.store.get();
    const span = tl.t1 - tl.t0;
    const key = e.key;
    // With something selected the arrows walk its row, item by item; shift, or no
    // selection, pans by a tenth of the window as the chip page does.
    if (key === "ArrowLeft" || key === "ArrowRight") {
      const dir = key === "ArrowRight" ? 1 : -1;
      if (e.shiftKey || !this.stepSelection(dir)) tl.setView(tl.t0 + dir * span * 0.1, tl.t1 + dir * span * 0.1);
    }
    else if (key === "+" || key === "=") tl.zoomAt(tl.W / 2, 0.5);
    else if (key === "-") tl.zoomAt(tl.W / 2, 2);
    else if (key === "f") tl.fitAll();
    else if (key === "n") this.busy(1);
    else if (key === "p") this.busy(-1);
    else if (key === "z") this.zoomSelection();
    else if (key === "1") this.toggleSub("units");
    else if (key === "2") this.toggleSub("dma");
    else if (key === "Escape") { this.store.set({ selection: null }); tl.setArrows([]); this.detail.show(null); tl.invalidate(2); }
    else if (key === "s") { const rows = this.stalls.rows; if (rows.length) { this.stallIdx = ((this.stallIdx ?? -1) + 1) % Math.min(rows.length, 60); const x = rows[this.stallIdx]; this.pickWait(x.w, x.r, x.row); } }
    else if (key === "[" || key === "]") {
      const row = s.selection ? s.selection.row : (s.hlRow || null);
      if (!row) return;
      const ids = this.links.waitsByRow.get(row.split(".")[0]);
      if (!ids || !ids.length) return;
      const cur = s.selection && s.selection.kind === "wait" ? ids.indexOf(s.selection.wait.i) : -1;
      const nxt = key === "]" ? Math.min(ids.length - 1, cur + 1) : Math.max(0, cur - 1);
      const w = this.model.waits[ids[nxt]];
      this.pickWait(w, this.links.releaserOf(w), rowKeyOf(w.chip, w.core, w.ci));
    } else return;
    e.preventDefault();
  }

  // The previous or next item of the selection's kind on its row: the neighbouring
  // bundle of a stream, wait, stall, DMA, flag write or host command. Returns false
  // when nothing is selected that has neighbours. The view follows the item when it
  // would otherwise be off screen.
  stepSelection(dir) {
    const sel = this.store.get().selection, m = this.model, links = this.links;
    if (!sel) return false;
    const rowKey = sel.core ? sel.core.key : sel.row ? String(sel.row).split(".")[0] : null;
    let next = null, at = NaN;
    if (sel.kind === "bundle" || sel.kind === "nop") {
      const c = sel.core;
      let bi = (sel.kind === "nop" ? m.bundleAt(c, dir > 0 ? sel.end : sel.start) : sel.bi) + dir;
      while (bi >= 0 && bi < c.n && Number.isNaN(c.t[bi])) bi += dir;
      if (bi < 0 || bi >= c.n) return true;
      next = { kind: "bundle", row: c.key, core: c, bi }; at = c.t[bi];
    } else if (sel.kind === "wait" || sel.kind === "stall") {
      const ids = (sel.kind === "wait" ? links.waitsByRow : links.stallsByRow).get(rowKey);
      const arr = sel.kind === "wait" ? m.waits : m.stalls, cur = sel.kind === "wait" ? sel.wait : sel.stall;
      const k = ids ? ids.indexOf(cur.i) + dir : -1;
      if (!ids || k < 0 || k >= ids.length) return true;
      const x = arr[ids[k]];
      if (sel.kind === "wait") { this.pickWait(x, links.releaserOf(x), rowKeyOf(x.chip, x.core, x.ci)); return true; }
      next = { kind: "stall", row: sel.row, stall: x }; at = x.t0;
    } else if (sel.kind === "dma" || sel.kind === "flag") {
      const ids = (sel.kind === "dma" ? links.dmaByIssuer : links.flagsByRow).get(rowKey);
      const arr = sel.kind === "dma" ? m.dmas : m.flags, cur = sel.kind === "dma" ? sel.dma : sel.flag;
      const k = ids ? ids.indexOf(cur.i) + dir : -1;
      if (!ids || k < 0 || k >= ids.length) return true;
      const x = arr[ids[k]];
      next = sel.kind === "dma" ? { kind: "dma", row: sel.row, dma: x } : { kind: "flag", row: sel.row, flag: x };
      at = sel.kind === "dma" ? (Number.isNaN(x.at) ? x.start : x.at) : x.at;
    } else if (sel.kind === "host") {
      const evs = m.hostByChip.get(sel.host.chip) || [];
      const k = evs.indexOf(sel.host) + dir;
      if (k < 0 || k >= evs.length) return true;
      const x = evs[k];
      next = { kind: "host", row: sel.row, host: x }; at = x.span ? x.span[0] : x.ps;
    } else return false;
    this.select(next);
    const tl = this.tl, x = tl.x(at);
    if (x < 0 || x > tl.W) { const span = tl.t1 - tl.t0; tl.setView(at - span / 2, at + span / 2); }
    return true;
  }

  // --- hash ------------------------------------------------------------------------

  toHash() {
    if (!this.tl) return {};
    if (this.page === "chip") return { page: "chip", ...this.chipPage.toHash() };
    const s = this.store.get();
    const sel = s.selection;
    return {
      w: `${Math.round(this.tl.t0)}-${Math.round(this.tl.t1)}`,
      chips: [...this.expandedChips].join(","),
      open: [...this.openCores].join(","),
      sub: Object.keys(this.sub).filter((k) => this.sub[k]).map((k) => k[0]).join(""),
      q: this.filter,
      sel: sel ? (sel.kind === "wait" ? "wait:" + sel.wait.i : sel.kind === "dma" ? "dma:" + sel.dma.i : sel.kind === "bundle" ? `bundle:${sel.core.key}:${sel.bi}` : sel.kind === "host" ? "host:" + sel.host.i : "") : "",
    };
  }

  applyHash(h) {
    if (!h) return;
    if (h.page === "chip" && h.chip != null && h.chip !== "") {
      this.openChip(+h.chip, h.tc === "" || h.tc == null ? null : (/^\d+$/.test(h.tc) ? +h.tc : h.tc), h.t ? +h.t : null);
      if (h.w && this.chipPage.tl) { const [a, b] = h.w.split("-").map(Number); if (b > a) this.chipPage.tl.setView(a, b); }
      return;
    }
    if (h.chips != null) { this.expandedChips = new Set(h.chips.split(",").filter((x) => x !== "").map(Number)); }
    if (h.open != null) this.openCores = new Set(h.open.split(",").filter(Boolean));
    if (h.sub != null) { for (const k of Object.keys(this.sub)) this.sub[k] = h.sub.includes(k[0]); for (const k in this.subBtns) this.subBtns[k].classList.toggle("on", this.sub[k]); }
    if (h.q) { this.filter = h.q; this.filterInp.value = h.q; }
    this.rebuildRows();
    if (h.w) { const [a, b] = h.w.split("-").map(Number); if (b > a) this.tl.setView(a, b); }
    if (h.sel) {
      const [kind, a, b] = h.sel.split(":");
      const m = this.model;
      if (kind === "wait" && m.waits[+a]) { const w = m.waits[+a]; this.select({ kind: "wait", row: rowKeyOf(w.chip, w.core, w.ci), wait: w }); }
      else if (kind === "dma" && m.dmas[+a]) this.selectDma(m.dmas[+a]);
      else if (kind === "bundle" && m.coreByKey.get(a)) { const c = m.coreByKey.get(a); this.select({ kind: "bundle", row: c.key, core: c, bi: +b }); }
      else if (kind === "host" && m.host[+a]) { const hh = m.host[+a]; this.select({ kind: "host", row: `c${hh.chip}host`, host: hh }); }
    }
  }

  syncHash() { this.store.set({ _tick: (this.store.get()._tick || 0) + 1 }); }
}

void $; void fmtPs; void unitColor;
